import { CONSOLE_ROLES, type ConsoleRole } from "@flexfreight/core";
import { Button, Dialog, notify, Select, TextField, useConfirm, type SelectOption } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../../api/client";
import { carrierName } from "../freight/Shipments";
import { ScheduleDialog } from "./ScheduleDialog";
import { describeParams, ROLE_LABEL, sameParams, type ReportParams, type SavedView } from "./reportsApi";

const SAVE = "__save";
const UPDATE = "__update";
const EMAIL = "__email";
const MANAGE = "__manage";
const EMPTY = "__empty";
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
type Mode = { kind: "idle" } | { kind: "save" } | { kind: "rename"; view: SavedView } | { kind: "manage" } | { kind: "email"; view: SavedView };

/**
 * Saved views for one report, as a toolbar menu (views, then save / update / email / manage actions) plus the
 * dialogs it opens. Same shape as the portal's shipments saved views, but stored server side so a view can be
 * shared with a role and scheduled by email. `menu` goes in the FilterToolbar's trailing slot, `overlays` at page level.
 */
export function useReportViews({ reportId, params, viewId, onApply }: { reportId: string; params: ReportParams; viewId: string | null; onApply: (p: ReportParams, viewId: string | null) => void }): {
  menu: ReactNode;
  overlays: ReactNode;
  active: SavedView | null;
} {
  const qc = useQueryClient();
  const views = useQuery({ queryKey: ["console", "report-views", reportId], queryFn: () => api<{ views: SavedView[] }>(`/v1/console/report-views?report=${reportId}`) });
  const list = views.data?.views ?? [];
  const selected = list.find((v) => v.id === viewId) ?? null;
  const active = selected && sameParams(selected.config, params) ? selected : (list.find((v) => sameParams(v.config, params)) ?? null);
  const modified = selected !== null && active?.id !== selected.id;
  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<"personal" | ConsoleRole>("personal");
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const formId = useId();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["console", "report-views"] });
    void qc.invalidateQueries({ queryKey: ["console", "report-schedules"] });
  };
  useEffect(() => {
    if (mode.kind === "save" || mode.kind === "rename") nameRef.current?.focus();
  }, [mode]);

  const save = useMutation({
    mutationFn: () =>
      mode.kind === "rename"
        ? api<{ view: SavedView }>(`/v1/console/report-views/${mode.view.id}`, { method: "PATCH", body: { name, visibility: visibility === "personal" ? "personal" : "role", sharedRole: visibility === "personal" ? null : visibility } })
        : api<{ view: SavedView }>("/v1/console/report-views", { method: "POST", body: { name, reportId, visibility: visibility === "personal" ? "personal" : "role", sharedRole: visibility === "personal" ? null : visibility, config: params } }),
    onSuccess: (r) => {
      refresh();
      notify.success(mode.kind === "rename" ? `Updated "${r.view.name}"` : `Saved view "${r.view.name}"`, r.view.visibility === "role" ? `Shared with ${ROLE_LABEL[r.view.sharedRole!]}.` : "Only you can see it.");
      if (mode.kind === "save") onApply(r.view.config, r.view.id);
      setMode(mode.kind === "rename" ? { kind: "manage" } : { kind: "idle" });
    },
    onError: (e) => {
      setError(message(e));
      nameRef.current?.focus();
    },
  });
  const update = useMutation({
    mutationFn: (v: SavedView) => api<{ view: SavedView }>(`/v1/console/report-views/${v.id}`, { method: "PATCH", body: { config: params } }),
    onSuccess: (r) => {
      refresh();
      notify.success(`Updated "${r.view.name}"`, "It now opens with these settings.");
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const remove = async (v: SavedView) => {
    const ok = await confirm({ title: `Delete "${v.name}"?`, body: v.schedules ? `Its ${v.schedules} email schedule${v.schedules === 1 ? "" : "s"} stop too. The report itself is not affected.` : "The saved settings are removed. The report itself is not affected.", confirmLabel: "Delete view", danger: true });
    if (!ok) return;
    try {
      await api(`/v1/console/report-views/${v.id}`, { method: "DELETE" });
      refresh();
      notify.success(`Deleted view "${v.name}"`);
      if (v.id === viewId) onApply(params, null);
    } catch (e) {
      notify.error("Not deleted", message(e));
    }
  };
  const openName = (m: Mode, initial: SavedView | null) => {
    setName(initial?.name ?? "");
    setVisibility(initial?.visibility === "role" && initial.sharedRole ? initial.sharedRole : "personal");
    setError(null);
    setMode(m);
  };
  const pick = (id: string) => {
    if (id === SAVE) return openName({ kind: "save" }, null);
    if (id === MANAGE) return setMode({ kind: "manage" });
    if (id === UPDATE && selected) return update.mutate(selected);
    if (id === EMAIL && active) return setMode({ kind: "email", view: active });
    const v = list.find((x) => x.id === id);
    if (v) onApply(v.config, v.id);
  };

  const options: SelectOption[] = [
    ...(list.length ? list.map((v) => ({ value: v.id, label: v.name, meta: v.visibility === "role" ? ROLE_LABEL[v.sharedRole!] : "Only me" })) : [{ value: EMPTY, label: "No saved views yet", disabled: true }]),
    { value: SAVE, label: active ? `Saved as "${active.name}"` : "Save current view…", action: true, disabled: !!active, icon: <span aria-hidden="true">+</span> },
    ...(modified && selected?.canEdit ? [{ value: UPDATE, label: `Update "${selected.name}" with these settings`, action: true, icon: <span aria-hidden="true">↻</span> }] : []),
    ...(active ? [{ value: EMAIL, label: "Email this report…", action: true, icon: <span aria-hidden="true">✉</span> }] : []),
    ...(list.length ? [{ value: MANAGE, label: "Manage views…", action: true, icon: <span aria-hidden="true">≡</span> }] : []),
  ];
  const menu = <Select variant="toolbar" label="View" className="ffa-views" options={options} value={active?.id ?? null} placeholder={modified ? `${selected!.name} (changed)` : "Unsaved"} onChange={pick} active={!!active} />;

  const naming = mode.kind === "save" || mode.kind === "rename";
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!name.trim()) {
      setError("Give the view a name");
      nameRef.current?.focus();
      return;
    }
    save.mutate();
  };
  const shareOptions = [{ value: "personal", label: "Only me" }, ...CONSOLE_ROLES.map((r) => ({ value: r, label: `Everyone in ${ROLE_LABEL[r]}` }))];
  const overlays = (
    <>
      <Dialog
        open={naming}
        onClose={() => setMode(mode.kind === "rename" ? { kind: "manage" } : { kind: "idle" })}
        initialFocus={nameRef}
        title={mode.kind === "rename" ? "Edit view" : "Save view"}
        description={mode.kind === "save" ? `Saves ${describeParams(params, carrierName)}.` : undefined}
        footer={
          <>
            <Button onClick={() => setMode({ kind: "idle" })}>Cancel</Button>
            <Button type="submit" form={formId} variant="primary" loading={save.isPending}>{mode.kind === "rename" ? "Save changes" : "Save view"}</Button>
          </>
        }
      >
        <form id={formId} noValidate onSubmit={submit} className="ffa-form">
          <TextField ref={nameRef} label="View name" example="Weekly XPO margin" maxLength={80} autoComplete="off" value={name} error={error} onChange={(e) => { setName(e.target.value); if (error) setError(null); }} />
          <Select label="Who can see it" value={visibility} onChange={(v) => setVisibility(v as "personal" | ConsoleRole)} options={shareOptions} hint="Owners and admins can see and manage every shared view." />
        </form>
      </Dialog>
      <Dialog open={mode.kind === "manage"} onClose={() => setMode({ kind: "idle" })} title="Saved views" footer={<Button onClick={() => setMode({ kind: "idle" })}>Done</Button>}>
        <ul className="ffa-viewlist" aria-label="Saved views">
          {list.map((v) => (
            <li key={v.id} className="ffa-viewlist__row">
              <span className="ffa-viewlist__text">
                <span className="ffa-cell-strong">{v.name}</span>
                <span className="ffa-small ffa-muted">{describeParams(v.config, carrierName)} · {v.visibility === "role" ? `Shared with ${ROLE_LABEL[v.sharedRole!]}` : "Only me"}{v.owner.isMe ? "" : ` · by ${v.owner.name ?? "someone"}`}{v.schedules ? ` · ${v.schedules} email schedule${v.schedules === 1 ? "" : "s"}` : ""}</span>
              </span>
              <span className="ffa-rowactions">
                <Button size="sm" variant="ghost" aria-label={`Email ${v.name}`} onClick={() => setMode({ kind: "email", view: v })}>Email…</Button>
                {v.canEdit ? <Button size="sm" variant="ghost" aria-label={`Edit view ${v.name}`} onClick={() => openName({ kind: "rename", view: v }, v)}>Edit</Button> : null}
                {v.canEdit ? <Button size="sm" variant="ghost" aria-label={`Delete view ${v.name}`} onClick={() => void remove(v)}>Delete</Button> : null}
              </span>
            </li>
          ))}
        </ul>
        {confirmDialog}
      </Dialog>
      <ScheduleDialog view={mode.kind === "email" ? mode.view : null} open={mode.kind === "email"} onClose={() => setMode({ kind: "idle" })} />
    </>
  );
  return { menu, overlays, active };
}
